import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { chatbotGif } from '../../assets/hostedassets';
import { prefersReducedMotion } from '../../utils/prefersReducedMotion';
import styles from './AskAicaOrb.module.scss';
import { AskAicaOrbProps, Phase, PlayMode, TextWidths } from './AskAicaOrb.types';
import {
    CLOSE_MS,
    DEFAULT_LABEL,
    EXPAND_MS,
    HOLD_MS,
    HOVER_LABEL,
    IDLE_EXPAND_MS,
    IDLE_HOLD_MS,
    ORB_HOLD_MS,
    READY_SETTLE_MS,
    REVEAL_MS,
    SECOND_MS,
    SHORT_ORB_HOLD_MS,
    SWAP_OUT_MS,
} from './AskAicaOrb.constants';
import { decidePlayMode, getCompanyPrompts, markPlayed } from './AskAicaOrb.utils';

/** The shared "Ask AICA" orb — Portfolio's DESKTOP orb and both the AICA
 * report's desktop and mobile orbs render this; those three stay visually
 * synced. Portfolio's mobile orb deliberately does NOT use this — it shipped
 * as a plain icon-only circle and stays that way. Presentational only; the
 * caller's own button/wrapper owns positioning and the click handler. */
function AskAicaOrb({ expandOnMount = false, ready = true, prompts = getCompanyPrompts(), playKey, className }: AskAicaOrbProps) {
    const [playMode] = useState<PlayMode>(() => {
        if (!expandOnMount) return 'full';
        return prefersReducedMotion() ? 'static' : decidePlayMode(playKey);
    });
    const [phase, setPhase] = useState<Phase>(() => {
        if (!expandOnMount) return 'orb';
        return playMode === 'static' ? 'idle' : 'hidden';
    });
    const startedRef = useRef(false);
    const timersRef = useRef<number[]>([]);
    const [firstText, secondText] = prompts;
    const firstMeasureRef = useRef<HTMLSpanElement>(null);
    const secondMeasureRef = useRef<HTMLSpanElement>(null);
    const idleMeasureRef = useRef<HTMLSpanElement>(null);
    const hoverMeasureRef = useRef<HTMLSpanElement>(null);
    const [textWidths, setTextWidths] = useState<TextWidths | null>(null);
    const isRendered = phase !== 'hidden';

    // Size each pill to hug its text: measure the real rendered text widths
    // from invisible copies (.Measure). Re-measured once web fonts finish
    // loading, since Gilroy arriving late would otherwise leave the pill sized
    // to the fallback font — and whenever the texts change (e.g. the company
    // name arrives).
    useLayoutEffect(() => {
        if (!expandOnMount || !isRendered) return;
        let cancelled = false;
        const measure = () => {
            const width = (el: HTMLSpanElement | null) => Math.ceil(el?.getBoundingClientRect().width ?? 0);
            const first = width(firstMeasureRef.current);
            const second = width(secondMeasureRef.current);
            const idle = width(idleMeasureRef.current);
            const hover = width(hoverMeasureRef.current);
            if (!cancelled && first > 0 && second > 0 && idle > 0 && hover > 0) {
                setTextWidths({ first, second, idle, hover });
            }
        };
        measure();
        document.fonts?.ready.then(measure);
        return () => {
            cancelled = true;
        };
    }, [expandOnMount, isRendered, firstText, secondText]);

    // Starts once, the first time `ready` is true, and then runs to the end
    // even if `ready` flips back and forth (the page's data briefly reloads
    // when switching modules, which used to restart the whole sequence).
    useEffect(() => {
        if (!expandOnMount || !ready || startedRef.current || playMode === 'static') return;
        startedRef.current = true;
        if (playKey) {
            markPlayed(playKey);
        }

        const orbAt = READY_SETTLE_MS;
        if (playMode === 'short') {
            const revealingAt = orbAt + SHORT_ORB_HOLD_MS;
            timersRef.current = [
                window.setTimeout(() => setPhase('orb'), orbAt),
                window.setTimeout(() => setPhase('revealing'), revealingAt),
                window.setTimeout(() => setPhase('idle'), revealingAt + IDLE_EXPAND_MS),
            ];
            return;
        }
        const expandingAt = orbAt + ORB_HOLD_MS;
        const mergedAt = expandingAt + EXPAND_MS;
        const expandedAt = mergedAt + REVEAL_MS;
        const swappingAt = expandedAt + HOLD_MS;
        const secondAt = swappingAt + SWAP_OUT_MS;
        const closingAt = secondAt + SECOND_MS;
        const closedAt = closingAt + CLOSE_MS;
        const revealingAt = closedAt + IDLE_HOLD_MS;
        const idleAt = revealingAt + IDLE_EXPAND_MS;
        timersRef.current = [
            window.setTimeout(() => setPhase('orb'), orbAt),
            window.setTimeout(() => setPhase('expanding'), expandingAt),
            window.setTimeout(() => setPhase('merged'), mergedAt),
            window.setTimeout(() => setPhase('expanded'), expandedAt),
            window.setTimeout(() => setPhase('swapping'), swappingAt),
            window.setTimeout(() => setPhase('second'), secondAt),
            window.setTimeout(() => setPhase('closing'), closingAt),
            window.setTimeout(() => setPhase('closed'), closedAt),
            window.setTimeout(() => setPhase('revealing'), revealingAt),
            window.setTimeout(() => setPhase('idle'), idleAt),
        ];
    }, [expandOnMount, ready, playMode, playKey]);

    // Timers are only cleared on unmount, not when `ready` changes.
    useEffect(
        () => () => {
            timersRef.current.forEach(window.clearTimeout);
            timersRef.current = [];
            startedRef.current = false;
        },
        [],
    );

    if (phase === 'hidden') return null;

    // Static orb: the always-fully-visible single "Ask AICA" pill.
    if (!expandOnMount) {
        return (
            <div className={clsx(styles.Pill, className)} data-testid="ask-aica-orb" data-phase="collapsed">
                <span className={styles.Label}>{DEFAULT_LABEL}</span>
                <span className={styles.Orb}>
                    <img src={chatbotGif} alt="" />
                </span>
            </div>
        );
    }

    // Text only appears once the pill has finished growing out, never during
    // the grow itself. Keyed by text so each new text restarts its own
    // animation; the first text keeps the same element from 'merged' into
    // 'expanded', so its blur sweep isn't restarted.
    let label: string | null = null;
    let labelAnimation: string | undefined;
    if (phase === 'merged') {
        label = firstText;
        labelAnimation = styles.LabelReveal;
    } else if (phase === 'expanded') {
        label = firstText;
    } else if (phase === 'swapping') {
        label = firstText;
        labelAnimation = styles.LabelRecede;
    } else if (phase === 'second') {
        label = secondText;
        labelAnimation = styles.LabelShimmer;
    } else if (phase === 'closing') {
        label = secondText;
        labelAnimation = styles.LabelRecede;
    } else if (phase === 'idle') {
        label = DEFAULT_LABEL;
        // Sweeps in in the AICA gradient, then settles to #4d4d4d — unless
        // it's already been shown for this company (static), then it just sits.
        labelAnimation = playMode === 'static' ? undefined : clsx(styles.LabelReveal, styles.LabelGradientToSolid);
    }

    const labelElement = label && (
        <span
            key={label}
            className={clsx(styles.SequenceLabel, labelAnimation)}
            title={label}
            data-text={label} // .LabelGradientToSolid's gradient copy
        >
            {label}
        </span>
    );

    return (
        <div
            className={clsx(styles.Anchor, className)}
            data-testid="ask-aica-orb"
            data-phase={phase}
            style={
                textWidths
                    ? ({
                          '--ask-aica-text-1': `${textWidths.first}px`,
                          '--ask-aica-text-2': `${textWidths.second}px`,
                          '--ask-aica-idle-text': `${textWidths.idle}px`,
                          '--ask-aica-hover-text': `${textWidths.hover}px`,
                      } as CSSProperties)
                    : undefined
            }
        >
            <span ref={firstMeasureRef} className={styles.Measure} data-text={firstText} aria-hidden />
            <span ref={secondMeasureRef} className={styles.Measure} data-text={secondText} aria-hidden />
            <span ref={idleMeasureRef} className={styles.Measure} data-text={DEFAULT_LABEL} aria-hidden />
            <span ref={hoverMeasureRef} className={styles.Measure} data-text={HOVER_LABEL} aria-hidden />
            {/* Clips the question pill to where it has come out from behind the orb. */}
            <div className={styles.TextClip}>
                <div className={styles.TextPill}>
                    {label &&
                        (phase === 'idle' ? (
                            // Settled: hover blurs "Ask AICA" out and "Start a conversation" in.
                            <span className={styles.TextSwap}>
                                <span className={styles.TextSwapDefault}>{labelElement}</span>
                                <span className={styles.TextSwapHover} aria-hidden>
                                    <span className={clsx(styles.SequenceLabel, styles.HoverLabel)}>{HOVER_LABEL}</span>
                                </span>
                            </span>
                        ) : (
                            labelElement
                        ))}
                </div>
            </div>
            <div className={styles.OrbPill}>
                <span className={styles.Orb}>
                    <img src={chatbotGif} alt="" />
                </span>
            </div>
        </div>
    );
}

export default AskAicaOrb;
